import type { AppCategory, AppDefinition } from "../core/Core.api";

// Important! Make sure the directory for your app is unique!
//
// Every app declares required metadata: a one-sentence `description` and the
// audience tiers that can see it (`audiences`). New apps default to ["rnd"]
// (R&D team review), then get promoted to "internal" and beyond. R&D users
// see every deployed app; see client/core/services/audience.ts for the rules.

export const kRootAppsDirectory = "apps";

export const getAppHref = (app: Readonly<AppDefinition>) =>
    `/${kRootAppsDirectory}/${app.directory}${app.directory.endsWith("/") ? "" : "/"}`;

export const exampleApp: AppDefinition = {
    title: "Example App",
    description: "Reference implementation demonstrating the standard app architecture",
    directory: "utility/example",
    audiences: ["rnd"],
};

export const designSystemApp: AppDefinition = {
    title: "Design System",
    description: "Gallery of the shared design-system components and styles",
    directory: "utility/design-system",
    audiences: ["rnd"],
};

export const allCapabilitiesApp: AppDefinition = {
    title: "All Capabilities",
    description: "Workbench exercising every Splice + third-party capability",
    directory: "utility/all-capabilities",
    audiences: ["internal"],
};

export const spliceSamplerApp: AppDefinition = {
    title: "Splice Sampler",
    description: "Pad sampler for chopping, tuning, and performing sounds from your library",
    directory: "arthur/splice-sampler",
    audiences: ["internal"],
    author: "Arthur",
};

export const variationsCompareApp: AppDefinition = {
    title: "Variations Compare",
    description: "Compare generated variations of the same input side by side",
    directory: "jeff/variations-compare",
    audiences: ["internal"],
    author: "Jeff",
};

export const voiceToSoundDrumsApp: AppDefinition = {
    title: "Voice to Sound: Drums",
    description: "Turn voice notes (beatbox, clap, tap) into a drum loop",
    directory: "jeff/voice-to-sound-drums",
    audiences: ["internal"],
    category: "Tools",
    author: "Jeff",
};

export const loopToDrumKitApp: AppDefinition = {
    title: "Loop to Drum Kit",
    description: "Turn drum loops into kits with matching Splice oneshots",
    directory: "jeff/loop-to-drum-kit",
    audiences: ["internal"],
    category: "Tools",
    author: "Jeff",
};

export const customComponentsApp: AppDefinition = {
    title: "Custom Components",
    description: "Playground for one-off custom UI components",
    directory: "utility/custom-components",
    audiences: ["rnd"],
};

export const variations20App: AppDefinition = {
    title: "Variations",
    description:
        "Generates AI variations of a loop, preserving BPM and rhythmic structure while altering harmonic content",
    directory: "splice/variations-2.0",
    audiences: ["internal"],
    version: "v2.0",
};

export const blendApp: AppDefinition = {
    title: "Blend",
    description:
        "Blends two audio loops into a single harmonically coherent output using pitch-palette conditioning",
    directory: "splice/blend",
    audiences: ["internal"],
    version: "v2.0",
};

export const magicFit25App: AppDefinition = {
    title: "Fit",
    description:
        "Fit using the SpliceGen 2.5 sparse pitch palette model for finer-grained timbre control",
    directory: "splice/magic-fit-2.5",
    audiences: ["internal"],
    version: "v2.5",
};

export const variations25App: AppDefinition = {
    title: "Variations",
    description:
        "Variations using the SpliceGen 2.5 sparse pitch palette model for richer harmonic expression",
    directory: "splice/variations-2.5",
    audiences: ["internal"],
    version: "v2.5",
};

export const variations30App: AppDefinition = {
    title: "Variations",
    description:
        "Variations using the SpliceGen 3.0 sparse pitch palette model for richer harmonic expression",
    directory: "splice/variations-3.0",
    audiences: ["internal", "soundcheck", "partner"],
    version: "v3.0",
};

export const blend30App: AppDefinition = {
    title: "Blend",
    description:
        "Blends two audio loops along a single symmetric knob using the SpliceGen 3.0 model, trading timbre against structure between the two sources",
    directory: "splice/blend-3.0",
    audiences: ["internal", "soundcheck", "partner"],
    version: "v3.0",
};

export const inpaintingApp: AppDefinition = {
    title: "Fill",
    description:
        "Regenerates a masked time range of an audio loop while preserving the surrounding audio, using the SpliceGen v3.5 fill model",
    directory: "splice/inpainting",
    audiences: ["internal", "soundcheck", "partner"],
    version: "v3.5",
};

export const inpaintingMagicFitApp: AppDefinition = {
    title: "Fill + Fit",
    // Kept in sync by hand with `kInpaintingMagicFitDescription`: this file is
    // imported by vite.config.ts before the `@/` alias exists, so it cannot
    // import from shared/.
    description: "Fill loops to fit another sample",
    directory: "splice/inpainting-magic-fit",
    audiences: ["internal"],
    category: "Control",
    version: "v3.5",
    author: "Shahan",
};

export const keyChangerApp: AppDefinition = {
    title: "Modulate",
    // Kept in sync by hand with `kKeyChangerDescription`: this file is imported
    // by vite.config.ts before the `@/` alias exists, so it cannot import from
    // shared/.
    description: "Retune a sample keeping its timbre and rhythm intact",
    directory: "splice/key-changer",
    audiences: ["internal"],
    category: "Edit",
    version: "v3.5",
    author: "Shahan",
};

export const magicFit30App: AppDefinition = {
    title: "Fit",
    description:
        "Fit using the SpliceGen 3.0 model with top-k complexity control over the timbre transfer",
    directory: "splice/magic-fit-3.0",
    audiences: ["internal", "soundcheck", "partner"],
    version: "v3.0",
};

export const craftApp: AppDefinition = {
    title: "Craft",
    description:
        "Generates pitch-palette conditioned audio variations across all 12 chromatic notes using the Craft pipeline",
    directory: "splice/craft",
    audiences: ["internal", "soundcheck", "partner"],
    version: "v2.0",
};

export const interplayApp: AppDefinition = {
    title: "Play",
    description: "Get multiple loops to play together",
    directory: "shahan/interplay",
    audiences: ["internal"],
    category: "Arrange",
    author: "Shahan",
};

export const aToBApp: AppDefinition = {
    title: "A to B",
    description: "Take a stack to its next section",
    directory: "shahan/a-to-b",
    audiences: ["internal"],
    category: "Arrange",
    author: "Shahan",
};

export const variationsCompare2530App: AppDefinition = {
    title: "Variations Compare v2.5 / v3.0",
    description: "Compare SpliceGen 2.5 and 3.0 variations of the same input side by side",
    directory: "shahan/variations-compare-2.5-3.0",
    audiences: ["internal"],
    author: "Shahan",
};

export const riffApp: AppDefinition = {
    title: "Riff",
    description: "Deep sample editing workflow using all Splice capabilities",
    directory: "rick/riff",
    audiences: ["internal", "soundcheck"],
    category: "Edit",
    author: "Rick",
};

export const variations40App: AppDefinition = {
    title: "Variations",
    description:
        "Variations using the SpliceGen 4.0 sparse pitch palette model for richer harmonic expression",
    directory: "splice/variations-4.0",
    audiences: ["rnd"],
    version: "v4.0",
};

export const magicFit40App: AppDefinition = {
    title: "Fit",
    description:
        "Fit using the SpliceGen 4.0 model with top-k complexity control over the timbre transfer",
    directory: "splice/magic-fit-4.0",
    audiences: ["rnd"],
    version: "v4.0",
};

export const inpainting40App: AppDefinition = {
    title: "Fill",
    description:
        "Regenerates a masked time range of an audio loop while preserving the surrounding audio, using the SpliceGen 4.0 inpainting model",
    directory: "splice/inpainting-4.0",
    audiences: ["rnd"],
    version: "v4.0",
};

export const inpaintingMagicFit40App: AppDefinition = {
    title: "Fill + Fit",
    // Kept in sync by hand with `kInpaintingMagicFit40Description`: this file is
    // imported by vite.config.ts before the `@/` alias exists, so it cannot
    // import from shared/.
    description: "Fill loops to fit another sample",
    directory: "splice/inpainting-magic-fit-4.0",
    audiences: ["rnd"],
    category: "Control",
    version: "v4.0",
    author: "Shahan",
};

export const stacksApp: AppDefinition = {
    title: "Stacks+",
    description: "Create Stacks with Splice sounds + make any loop fit",
    directory: "andy/stacks",
    audiences: ["internal", "soundcheck", "partner"],
    category: "Arrange",
    author: "Andy",
};

export const transcriptionCompareApp: AppDefinition = {
    title: "Transcriber",
    description: "Compare across multiple audio-to-MIDI models",
    directory: "andy/transcription-compare",
    audiences: ["rnd", "partner"],
    category: "Tools",
    extraCategories: ["Lyria 4"],
    author: "Andy",
};

export const deepEditMidiApp: AppDefinition = {
    title: "Note Edit",
    description:
        "Transcribe a sample, edit its notes, and Lyria 4 re-renders just the changed parts",
    directory: "andy/deep-edit-midi",
    audiences: ["rnd", "partner"],
    category: "Edit",
    extraCategories: ["Lyria 4"],
    author: "Andy",
};

export const deepEditDescribeApp: AppDefinition = {
    title: "Note Instruct",
    description: "Keep the notes, describe a new sound, and Lyria 4 renders it",
    directory: "andy/deep-edit-describe",
    audiences: ["rnd", "partner"],
    category: "Edit",
    extraCategories: ["Lyria 4"],
    author: "Andy",
};

export const stemEditorApp: AppDefinition = {
    title: "Stem Editor",
    description: "Edit stems with Lyria 4",
    directory: "andy/stem-editor",
    audiences: ["rnd", "partner"],
    category: "Edit",
    extraCategories: ["Lyria 4"],
    author: "Andy",
};

export const steeredVariationsApp: AppDefinition = {
    title: "Steered Variations",
    description: "Shape variations with the sound concepts in your loop",
    directory: "amantur/steered-variations",
    audiences: ["internal"],
    category: "Control",
    author: "Aman",
};

/**
 * One card in a section: an app, or an app's releases, newest first. A card
 * with releases opens the newest one the viewer can see, as a SpliceGen
 * capability card does, so it reaches a tier its newest release is hidden from.
 */
export type AppSectionEntry = AppDefinition | readonly AppDefinition[];

/** The releases an entry's card can open, newest first. */
export const getAppSectionEntryReleases = (entry: AppSectionEntry): readonly AppDefinition[] =>
    "directory" in entry ? [entry] : entry;

export interface AppSection {
    title: string;
    apps: AppSectionEntry[];
    badge?: string;
}

// The SpliceGen section is built from `SpliceGenCapabilities.ts`: one card per
// capability rather than one per model release.

export const kPeopleApps: AppSection = {
    title: "Apps",
    // Newest first, so the most recent work is what someone opening Labs sees.
    // A new app goes at the top of this list, with a `category` on its
    // definition so it lands under a tab as well as All.
    apps: [
        stemEditorApp,
        transcriptionCompareApp,
        deepEditMidiApp,
        deepEditDescribeApp,
        stacksApp,
        riffApp,
        interplayApp,
        aToBApp,
        [inpaintingMagicFit40App, inpaintingMagicFitApp],
        keyChangerApp,
        loopToDrumKitApp,
        voiceToSoundDrumsApp,
        steeredVariationsApp,
    ],
};

/**
 * The tabs over the Apps grid, in order, after All. A tab with no card the
 * viewer can open is left out for that viewer.
 */
export const kAppCategories: readonly AppCategory[] = [
    "Tools",
    "Edit",
    "Arrange",
    "Control",
    "Lyria 4",
];

/**
 * The tabs a card sits under: its newest release's `category` and
 * `extraCategories`. The releases of one card share them, which
 * `home-coverage.test.ts` checks.
 */
export const getAppSectionEntryCategories = (entry: AppSectionEntry): readonly AppCategory[] => {
    const newest = getAppSectionEntryReleases(entry).at(0);
    if (newest === undefined) {
        return [];
    }
    return [
        ...(newest.category === undefined ? [] : [newest.category]),
        ...(newest.extraCategories ?? []),
    ];
};

export const kUtilityApps: AppSection = {
    title: "Utilities",
    apps: [exampleApp, designSystemApp, customComponentsApp],
};
